/**
 * feat-165 Slice (frontend) — the explanatory-video detail pane.
 *
 * Hosts the whole authoring flow the approved React mockup enumerates
 * (design/video-item-editor.html), adapted from the mockup's inline `LessonBody`
 * mount (now test-only) to the LIVE feat-107 3-pane editor's right detail pane —
 * the same slot RehearsalEditor uses.
 *
 *  CREATE (existingId == null):
 *   §B source chooser (Record | Upload) → §C MediaRecorder capture OR §D file
 *   upload (client type/size/duration pre-check + S3 presigned-POST progress) →
 *   shared title+position footer → Save → presign → upload → createVideoItem.
 *   The parent then re-selects the new id, remounting this form in EDIT mode.
 *
 *  EDIT / REVIEW (existingId != null):
 *   Polls `captions_status` across the async gap (§E) — pending spinner / failed
 *   Retry / ready. When ready, §F caption review: EN is the source of truth, ES is
 *   auto-derived; editing an EN cue re-syncs its ES (PUT captions/en), an ES
 *   fine-tune sets a "manually edited — a future EN edit overwrites this" warning
 *   (PUT captions/es) with Reset-to-auto (reset-es). §G Publish renders the .vtt
 *   and flips the row live. Replace re-records/re-uploads; Delete soft-deletes.
 *
 * All captions_status styling reuses the StatusChip family (success/warning/danger)
 * — never brand coral for status. Backend contract: ADR 0001 D3/D4 (routes in
 * services/quizContentAdminService.ts).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  VIDEO_ALLOWED_TYPES,
  VIDEO_MAX_BYTES,
  VIDEO_MAX_DURATION_SECONDS,
  requestVideoUploadUrl,
  uploadVideoToS3,
  createVideoItem,
  replaceVideoItemSource,
  updateVideoItem,
  getVideoItemCaptions,
  updateVideoCaptionsEn,
  updateVideoCaptionEs,
  resetVideoCaptionEs,
  retryVideoCaptions,
  publishVideoItem,
} from '../../../services/quizContentAdminService';
import type {
  VideoItemSummary,
  VideoCaptionsPayload,
  VideoCaptionCue,
} from '../../../services/quizContentAdminService';
import ContentTimestamps from './ContentTimestamps';
import { useAutoSave, PanelSaveWiring, AutoSaveApi } from './useAutoSave';
import { ItemDetailFooter, SaveErrorBanner } from './ItemDetailFooter';
import {
  ApprovedValueLine,
  PanelApprovalState,
} from './approval/itemApproval';
import { QUIZ_TERMS } from '../quizTerminology';

// ── helpers ──────────────────────────────────────────────────────────────

/** m:ss from seconds (e.g. 48 → "0:48", 145 → "2:25"). */
function clock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

/** WebVTT-ish mm:ss.d for a cue timestamp (ms → "00:03.2"). Read-only display. */
function cueTime(ms: number): string {
  const s = ms / 1000;
  const m = Math.floor(s / 60);
  const rem = s - m * 60;
  return `${String(m).padStart(2, '0')}:${rem.toFixed(1).padStart(4, '0')}`;
}

function humanBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(0)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

/** Best-effort duration probe of a local video blob via a detached <video>. Some
 *  browsers report Infinity/NaN for MediaRecorder webm — return null then and let
 *  the backend ffprobe be authoritative (the create endpoint + job re-check). */
function readVideoDuration(blob: Blob): Promise<number | null> {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(blob);
      const el = document.createElement('video');
      el.preload = 'metadata';
      el.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(Number.isFinite(el.duration) && el.duration > 0 ? el.duration : null);
      };
      el.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      el.src = url;
    } catch {
      resolve(null);
    }
  });
}

/** A captured/selected clip staged for save. */
interface StagedClip {
  file: File;
  contentType: string;
  durationSeconds: number | null;
  previewUrl: string;
}

// ── shared small UI ──────────────────────────────────────────────────────

const Spinner: React.FC<{ tone?: 'video' | 'coral' | 'warning'; size?: number }> = ({
  tone = 'video',
  size = 24,
}) => {
  const border =
    tone === 'coral' ? 'border-coral' : tone === 'warning' ? 'border-warning-on' : 'border-video';
  return (
    <span
      className={`inline-block rounded-full border-2 ${border} border-t-transparent animate-spin`}
      style={{ width: size, height: size }}
      role="status"
      aria-label="Loading"
    />
  );
};

const ErrorBanner: React.FC<{ title: string; children?: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-2xl border border-danger-line/30 bg-danger-surface p-3 text-sm">
    <div className="flex items-start gap-2">
      <span className="text-danger text-lg leading-none">⚠</span>
      <div>
        <div className="font-semibold text-danger-text">{title}</div>
        {children && <div className="text-inkMuted mt-0.5">{children}</div>}
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// §C — In-browser recorder (MediaRecorder)
// ═══════════════════════════════════════════════════════════════════════════

const VideoRecorder: React.FC<{
  onClip: (clip: StagedClip) => void;
  onFallbackToUpload: () => void;
}> = ({ onClip, onFallbackToUpload }) => {
  const previewRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [phase, setPhase] = useState<'init' | 'ready' | 'recording' | 'recorded' | 'error'>('init');
  const [elapsed, setElapsed] = useState(0);
  const [recorded, setRecorded] = useState<StagedClip | null>(null);

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
  }, []);

  // Acquire camera + mic on mount.
  useEffect(() => {
    let cancelled = false;
    const md = navigator.mediaDevices;
    if (!md || typeof md.getUserMedia !== 'function' || typeof MediaRecorder === 'undefined') {
      setPhase('error');
      return;
    }
    md.getUserMedia({ video: true, audio: true })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (previewRef.current) {
          previewRef.current.srcObject = stream;
          previewRef.current.muted = true;
          previewRef.current.play().catch(() => undefined);
        }
        setPhase('ready');
      })
      .catch(() => {
        if (!cancelled) setPhase('error');
      });
    return () => {
      cancelled = true;
      stopTracks();
    };
  }, [stopTracks]);

  const start = () => {
    const stream = streamRef.current;
    if (!stream) return;
    const mimeType =
      typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.('video/webm')
        ? 'video/webm'
        : undefined;
    let rec: MediaRecorder;
    try {
      rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch {
      setPhase('error');
      return;
    }
    chunksRef.current = [];
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = async () => {
      if (tickRef.current) clearInterval(tickRef.current);
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const file = new File([blob], `recording-${Date.now()}.webm`, { type: 'video/webm' });
      const durProbe = await readVideoDuration(blob);
      const clip: StagedClip = {
        file,
        contentType: 'video/webm',
        // Prefer the timer (MediaRecorder webm often lacks a duration atom).
        durationSeconds: durProbe ?? (elapsed > 0 ? elapsed : null),
        previewUrl: URL.createObjectURL(blob),
      };
      setRecorded(clip);
      setPhase('recorded');
    };
    recorderRef.current = rec;
    rec.start();
    setElapsed(0);
    setPhase('recording');
    tickRef.current = setInterval(() => {
      setElapsed((e) => {
        const next = e + 1;
        if (next >= VIDEO_MAX_DURATION_SECONDS) stopRecording();
        return next;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  };

  const reRecord = () => {
    if (recorded) URL.revokeObjectURL(recorded.previewUrl);
    setRecorded(null);
    setElapsed(0);
    setPhase('ready');
  };

  if (phase === 'error') {
    return (
      <div className="rounded-2xl border border-danger-line/30 bg-danger-surface p-4 text-sm">
        <div className="font-semibold text-danger-text">Can't access camera or microphone</div>
        <p className="text-inkMuted mt-0.5">
          Bespoke needs permission to record. Allow access in your browser, or{' '}
          <button className="text-video font-semibold underline" onClick={onFallbackToUpload}>
            upload a file instead
          </button>
          .
        </p>
      </div>
    );
  }

  const remaining = VIDEO_MAX_DURATION_SECONDS - elapsed;

  return (
    <div className="space-y-3">
      <div className="aspect-video rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 relative overflow-hidden flex items-center justify-center">
        {phase === 'recorded' && recorded ? (
          <video src={recorded.previewUrl} controls className="w-full h-full object-contain bg-black" />
        ) : (
          <video ref={previewRef} playsInline className="w-full h-full object-cover" />
        )}
        {phase === 'init' && <span className="absolute text-white/70 text-sm">Starting camera…</span>}
        {phase === 'recording' && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] bg-danger text-white px-1.5 py-0.5 rounded">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> REC {clock(elapsed)}
          </span>
        )}
      </div>

      {phase === 'ready' && (
        <div className="flex flex-col items-center">
          <button
            onClick={start}
            className="w-12 h-12 rounded-full bg-coral flex items-center justify-center shadow hover:brightness-95"
            aria-label="Start recording"
          >
            <span className="w-5 h-5 rounded-full bg-white" />
          </button>
          <div className="text-[11px] text-inkMuted mt-1.5">Tap to start · limit {clock(VIDEO_MAX_DURATION_SECONDS)}</div>
        </div>
      )}
      {phase === 'recording' && (
        <div className="flex flex-col items-center">
          <button
            onClick={stopRecording}
            className="w-12 h-12 rounded-full bg-danger flex items-center justify-center shadow hover:brightness-95"
            aria-label="Stop recording"
          >
            <span className="w-4 h-4 rounded-[3px] bg-white" />
          </button>
          <div className="text-[11px] text-inkMuted mt-1.5">Tap to stop · {clock(remaining)} remaining</div>
        </div>
      )}
      {phase === 'recorded' && recorded && (
        <div className="flex gap-2">
          <button
            onClick={reRecord}
            className="flex-1 text-xs font-semibold rounded-full px-3 py-2 border border-softBorder text-ink hover:bg-cream"
          >
            ↺ Re-record
          </button>
          <button
            onClick={() => {
              stopTracks();
              onClip(recorded);
            }}
            className="flex-1 text-xs font-semibold rounded-full px-3 py-2 bg-video text-white hover:brightness-95"
          >
            Use this recording
          </button>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// §D — File upload (dropzone + client validation)
// ═══════════════════════════════════════════════════════════════════════════

const VideoUploader: React.FC<{ onClip: (clip: StagedClip) => void }> = ({ onClip }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const accept = async (file: File) => {
    setError(null);
    if (!VIDEO_ALLOWED_TYPES.includes(file.type)) {
      setError(`${file.name || 'That file'} isn't a supported video. Use MP4, MOV, or WebM.`);
      return;
    }
    if (file.size > VIDEO_MAX_BYTES) {
      setError(
        `${humanBytes(file.size)} exceeds the ${humanBytes(VIDEO_MAX_BYTES)} limit. Trim it or record a shorter clip.`,
      );
      return;
    }
    setChecking(true);
    const duration = await readVideoDuration(file);
    setChecking(false);
    if (duration != null && duration > VIDEO_MAX_DURATION_SECONDS + 0.5) {
      setError(
        `${clock(duration)} exceeds the ${clock(VIDEO_MAX_DURATION_SECONDS)} limit for an intro video.`,
      );
      return;
    }
    onClip({
      file,
      contentType: file.type,
      durationSeconds: duration,
      previewUrl: URL.createObjectURL(file),
    });
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) accept(file);
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
          dragOver ? 'border-video bg-video-light' : 'border-video-border bg-video-light/50'
        }`}
      >
        <div className="text-3xl">⬆️</div>
        <div className="font-semibold text-ink mt-2">Drop a video here</div>
        <div className="text-xs text-inkMuted mt-0.5">
          or{' '}
          <button className="text-video font-semibold underline" onClick={() => inputRef.current?.click()}>
            browse files
          </button>
        </div>
        <div className="text-[11px] text-inkMuted mt-3">
          MP4 / MOV / WebM · ≤ {clock(VIDEO_MAX_DURATION_SECONDS)} · ≤ {humanBytes(VIDEO_MAX_BYTES)}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) accept(file);
            e.target.value = '';
          }}
        />
      </div>
      {checking && (
        <div className="text-xs text-inkMuted flex items-center gap-2">
          <Spinner size={14} /> Checking the video…
        </div>
      )}
      {error && <ErrorBanner title="Can't use that file">{error}</ErrorBanner>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CREATE / REPLACE flow (chooser → record|upload → footer)
// ═══════════════════════════════════════════════════════════════════════════

/** feat-259 — copy that changed because Save left the panel. Constants, not
 *  literals: a literal-text grep must not return a true zero for on-screen copy. */
export const VIDEO_COPY = {
  /** The old label was "Save video → generate captions". A video item cannot
   *  exist without its file (`VideoItemCreate.source_s3_key` is required), so
   *  spec §Creating a new item makes THE COMPLETED UPLOAD the create. The button
   *  is therefore the UPLOAD trigger, not a Save, and it says so. */
  upload: 'Upload video → generate captions',
  replace: 'Replace video → regenerate captions',
  uploading: 'Uploading…',
  /** Replace mode is the one place an exit control is still legitimate: there IS
   *  something unsaved (a staged clip that has not uploaded), which is exactly
   *  what spec §Footer layout says Cancel no longer applies to elsewhere. It is
   *  named for what it DOES rather than being a Cancel. */
  keepCurrent: '✕ Keep current video',
  afterUpload:
    'The video uploads to S3, then a captions job runs (transcode + Deepgram EN + Claude ES). You review captions next.',
} as const;

const VideoCaptureFlow: React.FC<{
  mode: 'create' | 'replace';
  defaultTitle: string;
  defaultSort: number;
  saving: boolean;
  uploadFrac: number | null;
  error: string | null;
  onSave: (args: { clip: StagedClip; title: string; sortOrder: number }) => void;
  /** Replace mode only — abandons the replacement and keeps the current video.
   *  Absent in create mode: there, leaving means selecting a different item. */
  onKeepCurrent?: () => void;
}> = ({ mode, defaultTitle, defaultSort, saving, uploadFrac, error, onSave, onKeepCurrent }) => {
  const [source, setSource] = useState<'choose' | 'record' | 'upload'>('choose');
  const [clip, setClip] = useState<StagedClip | null>(null);
  const [title, setTitle] = useState(defaultTitle);
  const [sortOrder, setSortOrder] = useState(defaultSort);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-video">🎬 {mode === 'replace' ? 'Replace video' : 'New video item'}</h3>
        {onKeepCurrent && (
          <button className="text-inkMuted hover:text-ink text-sm" onClick={onKeepCurrent} disabled={saving}>
            {VIDEO_COPY.keepCurrent}
          </button>
        )}
      </div>

      {/* §B source chooser */}
      {!clip && source === 'choose' && (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              className="text-left rounded-2xl border-2 border-video bg-video-light p-4 hover:brightness-[0.98]"
              onClick={() => setSource('record')}
            >
              <div className="text-2xl">🎥</div>
              <div className="font-semibold text-ink mt-1">Record in-browser</div>
              <div className="text-xs text-inkMuted mt-0.5">
                Use this device's webcam &amp; mic. Up to {clock(VIDEO_MAX_DURATION_SECONDS)}.
              </div>
            </button>
            <button
              className="text-left rounded-2xl border-2 border-softBorder bg-white p-4 hover:bg-cream"
              onClick={() => setSource('upload')}
            >
              <div className="text-2xl">⬆️</div>
              <div className="font-semibold text-ink mt-1">Upload a file</div>
              <div className="text-xs text-inkMuted mt-0.5">
                MP4 / MOV / WebM · ≤ {clock(VIDEO_MAX_DURATION_SECONDS)} · ≤ {humanBytes(VIDEO_MAX_BYTES)}.
              </div>
            </button>
          </div>
          <p className="text-[11px] text-inkMuted">Either way, captions (EN + ES) generate automatically after save.</p>
        </>
      )}

      {/* §C record */}
      {!clip && source === 'record' && (
        <VideoRecorder onClip={setClip} onFallbackToUpload={() => setSource('upload')} />
      )}

      {/* §D upload */}
      {!clip && source === 'upload' && <VideoUploader onClip={setClip} />}

      {/* staged clip preview + footer */}
      {clip && (
        <>
          <div className="rounded-xl border border-success/40 bg-success-light p-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-xl">🎞️</span>
              <div className="min-w-0">
                <div className="font-medium text-ink truncate">{clip.file.name}</div>
                <div className="text-[11px] text-inkMuted">
                  {humanBytes(clip.file.size)}
                  {clip.durationSeconds != null ? ` · ${clock(clip.durationSeconds)}` : ''}
                </div>
              </div>
              <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-success">✓ Ready</span>
            </div>
            <button
              className="mt-2 text-xs text-inkMuted underline"
              onClick={() => {
                URL.revokeObjectURL(clip.previewUrl);
                setClip(null);
              }}
              disabled={saving}
            >
              Choose a different video
            </button>
          </div>

          {/* shared save footer */}
          <div className="grid sm:grid-cols-[1fr_120px] gap-3">
            <label className="block">
              <span className="text-xs text-inkMuted">Item title</span>
              <input
                className="mt-1 w-full rounded-lg border border-softBorder px-3 py-2 text-sm"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Welcome — how we greet"
              />
            </label>
            <label className="block">
              <span className="text-xs text-inkMuted">Position (sort)</span>
              <input
                type="number"
                className="mt-1 w-full rounded-lg border border-softBorder px-3 py-2 text-sm"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
              />
            </label>
          </div>

          {uploadFrac != null && (
            <div>
              <div className="h-2 rounded-full bg-cream-dark overflow-hidden">
                <div className="h-full bg-video transition-all" style={{ width: `${Math.round(uploadFrac * 100)}%` }} />
              </div>
              <div className="text-[11px] text-inkMuted mt-1">Uploading… {Math.round(uploadFrac * 100)}%</div>
            </div>
          )}

          {error && <ErrorBanner title="Couldn't save the video">{error}</ErrorBanner>}

          <div className="flex justify-end gap-2">
            {/* AC12 — this is the UPLOAD trigger, and the completed upload is
                what creates the item. The title and position above are held in
                LOCAL STATE until then and ride in the create call, so they are
                never written by a separate follow-up update. */}
            <button
              className="text-sm font-semibold rounded-full px-4 py-2 bg-coral text-white hover:brightness-95 disabled:opacity-40 inline-flex items-center gap-2"
              onClick={() => onSave({ clip, title: title.trim() || 'Untitled video', sortOrder })}
              disabled={saving}
            >
              {saving && <Spinner tone="coral" size={14} />}
              {saving ? VIDEO_COPY.uploading : mode === 'replace' ? VIDEO_COPY.replace : VIDEO_COPY.upload}
            </button>
          </div>
          <p className="text-[11px] text-inkMuted">{VIDEO_COPY.afterUpload}</p>
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// §F — Caption review (EN | ES)
// ═══════════════════════════════════════════════════════════════════════════

const CaptionReview: React.FC<{
  videoItemId: string;
  payload: VideoCaptionsPayload;
  onCuesChanged: (cues: VideoCaptionCue[]) => void;
}> = ({ videoItemId, payload, onCuesChanged }) => {
  const [cues, setCues] = useState<VideoCaptionCue[]>(payload.cues);
  const [enText, setEnText] = useState<Record<number, string>>(() =>
    Object.fromEntries(payload.cues.map((c) => [c.index, c.en])),
  );
  const [esText, setEsText] = useState<Record<number, string>>(() =>
    Object.fromEntries(payload.cues.map((c) => [c.index, c.es ?? ''])),
  );
  const [resyncing, setResyncing] = useState<Set<number>>(new Set());
  const [rowError, setRowError] = useState<Record<number, string>>({});

  /** The cue array this component's state was last built from. Seeded with the
   *  mount payload, which the `useState` initialisers above have ALREADY
   *  applied. */
  const appliedCuesRef = useRef<VideoCaptionCue[]>(payload.cues);

  useEffect(() => {
    // feat-259 — THE GUARD IS THE FIX, and it is not a micro-optimisation.
    //
    // On mount this effect used to re-apply the very cues the `useState`
    // initialisers had already applied. A passive effect runs AFTER the DOM is
    // painted, so there is a real window between the textareas appearing and
    // this effect flushing — and anything typed in that window was overwritten
    // by the server values. The editor sees their text revert with no request
    // and no error.
    //
    // It surfaced as this file's long-standing "~33% 0 calls" flake in
    // `VideoDetailForm.test.tsx`, which a previous fix had treated at the
    // symptom (a `waitFor` before the blur) on a stale-closure theory.
    //
    // A/B MEASURED, one variable, 6 scoped runs each (1013 tests):
    //   effect disabled entirely -> 0/6 failed
    //   effect restored          -> 4/6 failed
    // The effect's MOUNT RUN is the cause. Skipping the run that would re-apply
    // the identical array keeps the real job — replacing state when a genuinely
    // new payload arrives (a Retry re-poll) — and drops only the redundant one.
    if (appliedCuesRef.current === payload.cues) return;
    appliedCuesRef.current = payload.cues;
    setCues(payload.cues);
    setEnText(Object.fromEntries(payload.cues.map((c) => [c.index, c.en])));
    setEsText(Object.fromEntries(payload.cues.map((c) => [c.index, c.es ?? ''])));
  }, [payload.cues]);

  const setBusy = (index: number, on: boolean) =>
    setResyncing((prev) => {
      const next = new Set(prev);
      if (on) next.add(index);
      else next.delete(index);
      return next;
    });

  const mergeCue = (updated: VideoCaptionCue) => {
    setCues((prev) => {
      const next = prev.map((c) => (c.index === updated.index ? updated : c));
      onCuesChanged(next);
      return next;
    });
    setEsText((prev) => ({ ...prev, [updated.index]: updated.es ?? '' }));
  };

  const replaceAll = (updated: VideoCaptionCue[]) => {
    setCues(updated);
    onCuesChanged(updated);
    setEsText(Object.fromEntries(updated.map((c) => [c.index, c.es ?? ''])));
    setEnText(Object.fromEntries(updated.map((c) => [c.index, c.en])));
  };

  // EN blur → re-sync ES for that cue (only if the text actually changed).
  const commitEn = async (cue: VideoCaptionCue) => {
    const next = enText[cue.index] ?? '';
    if (next === cue.en) return;
    setRowError((p) => ({ ...p, [cue.index]: '' }));
    setBusy(cue.index, true);
    try {
      const res = await updateVideoCaptionsEn(videoItemId, [{ index: cue.index, en: next }]);
      replaceAll(res.cues);
    } catch (e) {
      setRowError((p) => ({ ...p, [cue.index]: String(e) }));
    } finally {
      setBusy(cue.index, false);
    }
  };

  // ES blur → manager override (es_manually_edited = TRUE).
  const commitEs = async (cue: VideoCaptionCue) => {
    const next = esText[cue.index] ?? '';
    if (next === (cue.es ?? '')) return;
    setRowError((p) => ({ ...p, [cue.index]: '' }));
    setBusy(cue.index, true);
    try {
      const res = await updateVideoCaptionEs(videoItemId, { index: cue.index, es: next });
      mergeCue(res.cue);
    } catch (e) {
      setRowError((p) => ({ ...p, [cue.index]: String(e) }));
    } finally {
      setBusy(cue.index, false);
    }
  };

  const resetEs = async (cue: VideoCaptionCue) => {
    setRowError((p) => ({ ...p, [cue.index]: '' }));
    setBusy(cue.index, true);
    try {
      const res = await resetVideoCaptionEs(videoItemId, cue.index);
      mergeCue(res.cue);
    } catch (e) {
      setRowError((p) => ({ ...p, [cue.index]: String(e) }));
    } finally {
      setBusy(cue.index, false);
    }
  };

  if (cues.length === 0) {
    // §F empty — no speech detected.
    return (
      <div className="scene rounded-2xl border border-softBorder bg-white p-6 text-center">
        <div className="text-2xl">🤫</div>
        <div className="text-sm font-semibold text-ink mt-1">No speech detected</div>
        <p className="text-xs text-inkMuted mt-0.5">
          The transcription found no dialogue to caption. You can publish without captions, or replace the video with
          clearer audio.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-softBorder overflow-hidden bg-white">
      {/* source-of-truth banner */}
      <div className="flex items-center gap-2 bg-video-light border-b border-video-border/40 px-4 py-3">
        <span className="text-video text-lg">🔗</span>
        <div className="text-sm">
          <b className="text-ink">English is the source of truth.</b>{' '}
          <span className="text-inkMuted">
            Spanish is generated from it — edit English and Spanish re-syncs automatically. Timings are locked to the
            audio.
          </span>
        </div>
      </div>

      {/* column headers */}
      <div className="grid grid-cols-[56px_1fr_1fr] text-[11px] font-semibold uppercase tracking-wide text-inkMuted border-b border-softBorder">
        <div className="px-2 py-2">Time</div>
        <div className="px-2 py-2 flex items-center gap-1.5">
          🇺🇸 English
          <span className="normal-case font-normal text-[10px] text-video bg-video-light rounded px-1.5 py-0.5">
            editable · source
          </span>
        </div>
        <div className="px-2 py-2 flex items-center gap-1.5">
          🇲🇽 Spanish
          <span className="normal-case font-normal text-[10px] text-inkMuted bg-cream-dark rounded px-1.5 py-0.5">
            🔗 auto-synced
          </span>
        </div>
      </div>

      {cues.map((cue) => {
        const busy = resyncing.has(cue.index);
        const override = cue.es_manually_edited;
        const err = rowError[cue.index];
        return (
          <div
            key={cue.index}
            className="grid grid-cols-[56px_1fr_1fr] border-b border-softBorder items-stretch"
          >
            <div className="px-2 py-3 text-[11px] text-inkMuted font-mono leading-tight bg-cream/60">
              {cueTime(cue.start_ms)}
              <br />
              {cueTime(cue.end_ms)}
            </div>

            {/* EN — source */}
            <div className="px-2 py-3">
              <textarea
                className="w-full text-sm text-ink bg-white rounded-lg border border-softBorder focus:border-video focus:outline-none px-2.5 py-2 resize-none"
                rows={2}
                value={enText[cue.index] ?? ''}
                onChange={(e) => setEnText((p) => ({ ...p, [cue.index]: e.target.value }))}
                onBlur={() => commitEn(cue)}
                aria-label={`English caption ${cue.index + 1}`}
              />
            </div>

            {/* ES — derived / override */}
            <div className="px-2 py-3">
              {busy ? (
                <div className="w-full text-sm rounded-lg border border-softBorder px-2.5 py-2 bg-cream text-inkMuted min-h-[52px] flex items-center gap-2">
                  <Spinner size={14} /> Re-syncing Spanish…
                </div>
              ) : (
                <textarea
                  className={`w-full text-sm text-ink rounded-lg border px-2.5 py-2 resize-none focus:outline-none ${
                    override ? 'bg-white border-warning-on/60' : 'bg-cream border-softBorder focus:border-video'
                  }`}
                  rows={2}
                  value={esText[cue.index] ?? ''}
                  onChange={(e) => setEsText((p) => ({ ...p, [cue.index]: e.target.value }))}
                  onBlur={() => commitEs(cue)}
                  placeholder={cue.es == null ? 'Translation failed — edit or retry' : ''}
                  aria-label={`Spanish caption ${cue.index + 1}`}
                />
              )}
              {!busy && override && (
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 rounded border border-warning-on/40 bg-warning-surface text-warning-on text-[10px] font-semibold px-1.5 py-0.5">
                    ✎ Manually edited
                  </span>
                  <span className="text-[10px] text-warning-on">A future English edit will overwrite this.</span>
                  <button className="text-[10px] text-inkMuted underline" onClick={() => resetEs(cue)}>
                    Reset to auto
                  </button>
                </div>
              )}
              {!busy && !override && (
                <div className="text-[10px] text-inkMuted mt-1 flex items-center gap-1">🔗 mirrors English</div>
              )}
              {err && <div className="text-[10px] text-danger-text mt-1">Couldn't save — try again.</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// EDIT / REVIEW orchestrator (status poll → review → publish)
// ═══════════════════════════════════════════════════════════════════════════

const POLL_MS = 3000;

const VideoReviewPane: React.FC<{
  videoItemId: string;
  summary: VideoItemSummary | null;
  /** A reload-worthy change: publish, retry, replace. NOT a title edit — AC10. */
  onChanged: () => void;
  onReplace: () => void;
  /** feat-259 — the shared save controller for the one auto-saving field in
   *  this pane, the title. Owned by VideoDetailForm so the footer it drives is
   *  a sibling of this pane's scroll area rather than a child of it. */
  save: AutoSaveApi<VideoDraft>;
}> = ({ videoItemId, summary, onChanged, onReplace, save }) => {
  const [payload, setPayload] = useState<VideoCaptionsPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // Bumped to restart the poll loop after a Retry re-enqueues the job.
  const [pollNonce, setPollNonce] = useState(0);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      const p = await getVideoItemCaptions(videoItemId);
      setPayload(p);
      setLoadError(null);
      return p.captions_status;
    } catch (e) {
      setLoadError(String(e));
      return null;
    }
  }, [videoItemId]);

  // Initial load + poll while pending (survives container recycle — status is
  // DB-backed, D5). Stops as soon as status leaves 'pending'. Re-runs when
  // pollNonce bumps (a Retry re-enqueued the job).
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const status = await load();
      if (!cancelled && status === 'pending') pollRef.current = setTimeout(run, POLL_MS);
    };
    run();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [load, pollNonce]);

  // feat-259: the old `commitTitle` did three things this feature forbids. It
  // REFUSED an empty title and SILENTLY REVERTED the input to the server value
  // — "the typed value is never silently reverted to the server value" is the
  // rule AT8 exists to protect, and the backend slice is relaxing the matching
  // `min_length=1` so `''` is now a legal title. It also called `onChanged()`,
  // which reloads the whole drill on a blur (AT10). All three are gone: the
  // field goes through `useAutoSave` like every other text field in the feature.

  const doRetry = async () => {
    setRetrying(true);
    setActionError(null);
    try {
      await retryVideoCaptions(videoItemId);
      setPollNonce((n) => n + 1); // restart the poll loop (status is now 'pending')
      onChanged();
    } catch (e) {
      setActionError(String(e));
    } finally {
      setRetrying(false);
    }
  };

  const doPublish = async () => {
    setPublishing(true);
    setActionError(null);
    try {
      await publishVideoItem(videoItemId);
      await load();
      onChanged();
    } catch (e) {
      setActionError(String(e));
    } finally {
      setPublishing(false);
    }
  };

  if (!payload) {
    if (loadError) {
      return (
        <ErrorBanner title="Couldn't load captions">
          <button className="text-video font-semibold underline" onClick={() => load()}>
            Retry
          </button>
        </ErrorBanner>
      );
    }
    return (
      <div className="text-center py-8">
        <Spinner />
        <div className="text-sm text-inkMuted mt-2">Loading captions…</div>
      </div>
    );
  }

  const status = payload.captions_status;
  const published = payload.published;

  return (
    <div className="space-y-4">
      {/* feat-270 panels 4 / 9 — ONE panel-level block, never one per field. */}
      <PanelApprovalState />
      {/* header: title + status */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-video uppercase tracking-wide">🎬 Video</div>
          <input
            aria-label={VIDEO_FIELD_LABELS.title}
            className={`mt-1 w-full bg-transparent font-display text-lg font-semibold px-1 py-1 rounded-lg border ${
              save.error?.fields.includes('title') ? 'border-danger-line' : 'border-transparent'
            } hover:bg-white focus:bg-white focus:outline-none focus:ring-2 focus:ring-video/40`}
            value={save.draft.title}
            onChange={(e) => save.setField('title', e.target.value)}
            onBlur={() => save.commitField('title')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
          />
          {payload.duration_seconds != null && (
            <div className="text-xs text-inkMuted px-1 mt-0.5">{clock(payload.duration_seconds)}</div>
          )}
          {/* feat-252: `px-1` matches the duration line above, so both align to
              the title input's TEXT rather than its box (NOTES §3). Sourced from
              the tree summary — `VideoCaptionsPayload` has no timestamps. */}
          <ContentTimestamps
            variant="stacked"
            className="mt-0.5 px-1"
            created={summary?.created}
            updated={summary?.updated}
          />
          {/* §10.4 — same parent as the title input above. */}
          <ApprovedValueLine field="title" />
        </div>
      </div>

      {/* video preview */}
      {payload.video_url && (
        <video src={payload.video_url} controls playsInline className="w-full rounded-xl bg-black aspect-video" />
      )}

      {actionError && <ErrorBanner title="Something went wrong">{actionError}</ErrorBanner>}

      {/* §E status-driven body */}
      {status === 'pending' && (
        <div className="rounded-2xl border border-warning-on/30 bg-warning-surface/60 p-4 flex items-center gap-3">
          <span className="w-4 h-4 rounded-full border-2 border-warning-on border-t-transparent animate-spin inline-block" />
          <div className="text-sm">
            <div className="font-semibold text-warning-on">Generating captions…</div>
            <div className="text-inkMuted text-[11px]">
              Transcoding + transcribing + translating — usually under a minute. This page updates automatically.
            </div>
          </div>
        </div>
      )}

      {status === 'failed' && (
        <div className="rounded-2xl border border-danger-line/30 bg-danger-surface p-4">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-danger" />
            <span className="text-sm font-semibold text-danger-text">Captions failed</span>
          </div>
          <p className="text-[11px] text-inkMuted mt-1">
            The video is saved. Retry generating captions — if it fails again, tell your Bespoke contact; the
            reason is only recorded in the server logs.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              className="text-xs font-semibold rounded-full px-3 py-1.5 border border-danger-line/40 text-danger hover:bg-white disabled:opacity-40 inline-flex items-center gap-1.5"
              onClick={doRetry}
              disabled={retrying}
            >
              {retrying && <Spinner tone="warning" size={12} />}↺ Retry captions
            </button>
            <button
              className="text-xs font-semibold rounded-full px-3 py-1.5 border border-softBorder text-ink hover:bg-cream"
              onClick={onReplace}
            >
              Replace video
            </button>
          </div>
        </div>
      )}

      {status === 'ready' && (
        <>
          <CaptionReview videoItemId={videoItemId} payload={payload} onCuesChanged={() => undefined} />

          {/* §G publish footer */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              className="text-xs font-semibold rounded-full px-3 py-1.5 border border-softBorder text-ink hover:bg-cream"
              onClick={onReplace}
            >
              ↺ Replace video
            </button>
            {published ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-success-light text-success-on text-[11px] font-bold px-2 py-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-success" /> Published — live for servers
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-warning-on bg-warning-surface rounded px-1.5 py-0.5">
                ● Draft — not visible to servers yet
              </span>
            )}
            <button
              className="ml-auto text-sm font-semibold rounded-full px-5 py-2 bg-coral text-white hover:brightness-95 disabled:opacity-40 inline-flex items-center gap-2"
              onClick={doPublish}
              disabled={publishing}
            >
              {publishing && <Spinner tone="coral" size={14} />}
              {publishing ? 'Publishing…' : published ? 'Re-publish' : 'Publish video →'}
            </button>
          </div>
          <p className="text-[11px] text-inkMuted">
            Publishing renders the EN + ES caption tracks and makes the video visible at its position. Audited via
            change history (undoable).
          </p>
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Public entry — the right detail pane for a video item.
// ═══════════════════════════════════════════════════════════════════════════

/** The auto-saving fields of a video item. Position is fixed at create time by
 *  the capture flow and has no control in the review pane, so `title` is the
 *  whole of it — which is why this panel's conversion is the smallest of the
 *  five even though its FILE is the largest. */
export type VideoDraft = { title: string };

export const VIDEO_FIELD_LABELS: Record<keyof VideoDraft, string> = {
  title: 'Item title',
};

export interface VideoDetailFormProps extends PanelSaveWiring {
  tenantId: string;
  lessonId: string;
  /** null = create a new video item; otherwise edit/review this id. */
  existingId: string | null;
  /** The selected row (edit mode) — seeds title/status without a round-trip. */
  summary?: VideoItemSummary | null;
  /** Default sort position for a NEW item. */
  defaultSort: number;
  /** Fired ONCE, when the completed upload creates the row (AC12). */
  onCreated: (savedId: string) => void;
  /** Present in edit mode — soft-deletes the item. */
  onDelete?: () => void;
  /** A change that really does need the drill refetched: publish, caption
   *  retry, source replace. NOT a title edit. */
  onReloadNeeded?: () => void;
  /** The shell's item-type switcher for a NEW item — rendered inside the scroll
   *  child, since this component owns the `<aside>` now. */
  typeControl?: React.ReactNode;
}

export const VideoDetailForm: React.FC<VideoDetailFormProps> = ({
  tenantId,
  lessonId,
  existingId,
  summary,
  defaultSort,
  onCreated,
  onDelete,
  onReloadNeeded,
  typeControl,
  registerFlush,
  pendingNavigation = false,
  onNavigationProceed,
  onPatched,
}) => {
  const [saving, setSaving] = useState(false);
  const [uploadFrac, setUploadFrac] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Edit mode can enter a "replace" capture flow without leaving the item.
  const [replacing, setReplacing] = useState(false);

  const runUpload = useCallback(
    async (clip: StagedClip): Promise<string> => {
      const policy = await requestVideoUploadUrl(lessonId, {
        tenant_id: tenantId,
        content_type: clip.contentType,
        filename: clip.file.name,
      });
      setUploadFrac(0);
      await uploadVideoToS3(clip.file, policy, { onProgress: setUploadFrac });
      setUploadFrac(null);
      return policy.s3_key;
    },
    [lessonId, tenantId],
  );

  const save = useAutoSave<VideoDraft>({
    initial: { title: summary?.title ?? '' },
    itemId: existingId,
    labels: VIDEO_FIELD_LABELS,
    // No `create`: a video item cannot exist without its file, so the create is
    // the capture flow's job and never a blur's (spec §Creating a new item —
    // "Video is the one exception"). Reaching a create here would be a bug, and
    // omitting the callback makes it unrepresentable rather than merely unused.
    update: (id, changed) => updateVideoItem(id, changed),
    onPersisted: ({ id, changed }) => onPatched?.(id, changed as Record<string, unknown>),
  });

  useEffect(() => {
    registerFlush?.(save.flush);
    return () => registerFlush?.(null);
  }, [registerFlush, save.flush]);

  const handleNavRetry = async () => {
    if (await save.flush()) onNavigationProceed?.();
  };

  const handleCreate = async ({
    clip,
    title,
    sortOrder,
  }: {
    clip: StagedClip;
    title: string;
    sortOrder: number;
  }) => {
    setSaving(true);
    setError(null);
    try {
      const sourceKey = await runUpload(clip);
      const { id } = await createVideoItem(lessonId, {
        tenant_id: tenantId,
        title,
        sort_order: sortOrder,
        source_s3_key: sourceKey,
        duration_seconds: clip.durationSeconds != null ? Math.round(clip.durationSeconds) : null,
      });
      URL.revokeObjectURL(clip.previewUrl);
      onCreated(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setUploadFrac(null);
    } finally {
      setSaving(false);
    }
  };

  const handleReplace = async ({ clip }: { clip: StagedClip; title: string; sortOrder: number }) => {
    if (!existingId) return;
    setSaving(true);
    setError(null);
    try {
      const sourceKey = await runUpload(clip);
      await replaceVideoItemSource(existingId, { source_s3_key: sourceKey });
      URL.revokeObjectURL(clip.previewUrl);
      setReplacing(false);
      onReloadNeeded?.(); // re-select self → review pane re-polls
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setUploadFrac(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => onDelete?.();

  /** feat-259 / design/NOTES.md §3 — the RESTRUCTURE. All three states of this
   *  panel share one bounded `<aside>` with a scroll child plus `shrink-0`
   *  banner and footer siblings, so the footer is in the same place whether the
   *  editor is capturing, replacing, or reviewing. Dropping a footer into the
   *  old `overflow-y-auto` aside would have scrolled it away with the fields —
   *  feat-231's bug, passing a DOM-shape comparison while it did. */
  const shell = (body: React.ReactNode, deletable: boolean) => (
    <aside className="border-l border-softBorder bg-cream/40 flex flex-col min-h-0 overflow-hidden">
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        {typeControl}
        {body}
      </div>
      <SaveErrorBanner
        error={save.error as never}
        blockedReason={save.blockedReason}
        pendingNavigation={pendingNavigation}
        typeSingular={QUIZ_TERMS.video.singular}
        onRetry={pendingNavigation ? handleNavRetry : save.retry}
        onLeaveAnyway={onNavigationProceed}
      />
      <ItemDetailFooter
        onDelete={deletable && onDelete ? handleDelete : undefined}
        // While a clip is uploading the panel IS writing, even though no field
        // blurred — the status is a property of the panel, not of a field.
        status={saving ? 'saving' : save.status}
        savedAt={save.savedAt}
      />
    </aside>
  );

  // CREATE — nothing exists to delete until the completed upload creates it.
  if (existingId === null) {
    return shell(
      <VideoCaptureFlow
        mode="create"
        defaultTitle=""
        defaultSort={defaultSort}
        saving={saving}
        uploadFrac={uploadFrac}
        error={error}
        onSave={handleCreate}
      />,
      false,
    );
  }

  // EDIT → replacing
  if (replacing) {
    return shell(
      <VideoCaptureFlow
        mode="replace"
        defaultTitle={summary?.title ?? ''}
        defaultSort={summary?.sort_order ?? defaultSort}
        saving={saving}
        uploadFrac={uploadFrac}
        error={error}
        onSave={handleReplace}
        onKeepCurrent={() => {
          setReplacing(false);
          setError(null);
        }}
      />,
      true,
    );
  }

  // EDIT → review
  return shell(
    <VideoReviewPane
      videoItemId={existingId}
      summary={summary ?? null}
      onChanged={() => onReloadNeeded?.()}
      onReplace={() => setReplacing(true)}
      save={save}
    />,
    true,
  );
};

export default VideoDetailForm;
